import format from 'date-fns/format';
import { Component, Mixins } from 'vue-property-decorator';
import { mapActions } from 'vuex';
import { namespace } from 'vuex-class';
import {
  Breadcrumbs as BreadcrumbsLayout,
  Extra as ExtraLayout,
} from '@/components/layout';
import CalendarDay from '@/components/calendar/day';
import CalendarLink from '@/components/calendar/link';
import SelectCount from '@/components/parts/catalog/list/count';
import { Paginator as ActivitesPaginator } from '@/components/parts/pagination';
import CalendarText, {
  Variants as TextVariants,
} from '@/components/calendar/text';
import CatalogList from '@/components/parts/catalog/list';
import FlexCenter from '@/components/flex/center';
import { TransitionFade } from '@/components/transition';
import { PaginationLayout } from './components';
import List from '@/pages/mixin-list';
import PageError from '../../404.vue';
import Sections from '../sections';
import { Breadcrumbs } from '../types';
import {
  ACTION_FETCH,
  MONTHS,
  STORE_MODULE_NAME,
  TEXT,
  TITLE_DEFAULT,
  DATE_FORMAT_MASK,
} from './const';
import createStoreModule from './store';
import { State as StateType, Statuses } from './types';
import { CALENDAR_ROUTE, ACTIVITY_ROUTE } from '../const';
import { Dictionary } from '@/types';
import { isPast, isToday } from 'date-fns';

const { ERROR } = Statuses;
const StoreModule = namespace(STORE_MODULE_NAME);
const actions = mapActions(STORE_MODULE_NAME, [ACTION_FETCH]);
const fetch = actions[ACTION_FETCH];

const isError = (value: any) => value === ERROR;

const PAGE_SIZES = [7, 19, 39];
const PAGE_SIZE = PAGE_SIZES[1];
const PAGE_NUMBER = 1;

const BY_PAGE_TEXT = 'Мероприятий на странице';

const getPageSize = (value?: string) => value || PAGE_SIZE;

@Component({
  components: {
    PageError,
    FlexCenter,
    CalendarDay,
    CalendarLink,
    CalendarText,
    CatalogList,
    ExtraLayout,
    SelectCount,
    PaginationLayout,
    BreadcrumbsLayout,
    ActivitesPaginator,
    TransitionFade,
  },
  metaInfo() {
    const { dateString } = this as PageActivityDay;
    return { title: `${TITLE_DEFAULT} ${dateString}` };
  },
  storeName: STORE_MODULE_NAME,
  storeRegister: createStoreModule,
})
export default class PageActivityDay extends Mixins(Sections, List) {
  // Bindings
  @StoreModule.State
  items: StateType['items'];

  @StoreModule.State
  day: StateType['day'];

  @StoreModule.State
  month: StateType['month'];

  @StoreModule.State
  status: StateType['status'];

  @StoreModule.State
  year: StateType['year'];

  @StoreModule.State
  count: StateType['count'];

  @StoreModule.State
  limit: StateType['limit'];

  @StoreModule.State
  page: StateType['page'];

  readonly pageSizes = PAGE_SIZES;

  readonly byPageText = BY_PAGE_TEXT;

  // Computed
  get breadcrumbsExtended(): Breadcrumbs {
    const day = String(this.day);
    const month = String(this.month);
    const year = String(this.year);
    const params = { ...this.$route.params };
    return [
      ...this.breadcrumbSections,
      {
        text: year,
        to: {
          params,
          name: CALENDAR_ROUTE,
          query: { year },
        },
      },
      {
        text: MONTHS[month],
        to: {
          params,
          name: CALENDAR_ROUTE,
          query: { month, year },
        },
      },
      { text: day },
    ];
  }

  // Computed
  get activities() {
    return this.items.map(
      ({ announce, id, code, description, img, name, is_locked, cop_multi_name }) => {
        return {
          id,
          code,
          note: announce,
          text: description !== null ? description : '',
          image: {
            small: img || '',
            large: img || '',
          },
          link: {
            name: ACTIVITY_ROUTE,
            params: { code, id: String(id) },
            query: { date: format(this.getDate(), 'YYYY-MM-DD') },
          },
          title: name,
          cop_multi_name: cop_multi_name,
          is_locked,
        };
      },
    );
  }

  get calendar() {
    const { month, year } = this;
    const params = { ...this.$route.params };
    const query: Dictionary<string> = {
      ...this.$route.query,
      month: String(month),
      year: String(year),
    };
    delete query.date;
    delete query.pageSize;
    return {
      params,
      name: CALENDAR_ROUTE,
      query: query,
    };
  }

  get date() {
    return this.getDate();
  }

  get isCompleted() {
    const { date } = this;
    return isPast(date) && !isToday(date);
  }

  get dateString() {
    const { day, month, year } = this;
    return [day, MONTHS[month], year].join(' ');
  }

  get hasError(): boolean {
    return isError(this.status);
  }

  get groups() {
    return this.items;
  }

  get next() {
    const date = this.getDate();
    date.setDate(date.getDate() + 1);
    return {
      name: this.$route.name,
      params: { ...this.$route.params },
      query: {
        date: format(date, DATE_FORMAT_MASK),
      },
    };
  }

  get prev() {
    const date = this.getDate();
    date.setDate(date.getDate() - 1);
    return {
      name: this.$route.name,
      params: { ...this.$route.params },
      query: {
        date: format(date, DATE_FORMAT_MASK),
      },
    };
  }

  get ready() {
    return this.items.length > 0 && this.count === 0;
  }

  get text() {
    return TEXT;
  }

  get textVariants() {
    return TextVariants;
  }

  get waiting() {
    return this.count > 0;
  }

  get pageLimit() {
    return getPageSize(this.$route.query.limit as any);
  }

  get pageLimits() {
    return this.pageSizes.map((value) => ({ value, text: value }));
  }

  get pagesCount(): number {
    const { count, limit } = this;
    return limit ? Math.ceil(count / limit) : 0;
  }

  // AsyncData
  async asyncData({ store, route }: any) {
    await fetch.call(
      { $store: store },
      {
        page: PAGE_NUMBER,
        limit: PAGE_SIZE,
        ...route.params,
        ...route.query,
      },
    );
  }

  // Methods
  getDate() {
    const { day, month, year } = this;
    return new Date(year, month - 1, day);
  }
}



// WEBPACK FOOTER //
// ./src/pages/activities/day/script.ts